import { tracked } from "@glimmer/tracking";
import { tagFor, track, updateTag } from "@glimmer/validator";
import { meta as metaFor, peekMeta } from "@ember/-internals/meta";
import { TrackedDescriptor } from "@ember/-internals/metal";
import { next } from "@ember/runloop";
import { TrackedArray, TrackedSet } from "@ember-compat/tracked-built-ins";

/**
 * Define a tracked property on an object without needing to use the @tracked decorator.
 * Useful when meta-programming the creation of properties on an object.
 *
 * This must be run before the property is first accessed, so it normally makes sense for
 * this to only be called from a constructor.
 */
export function defineTrackedProperty(target, key, value) {
  Object.defineProperty(
    target,
    key,
    tracked(target, key, { enumerable: true, value })
  );
}

class ResettableTrackedState {
  @tracked currentValue;
  previousUpstreamValue;
}

function getOrCreateState(map, instance) {
  let state = map.get(instance);
  if (!state) {
    state = new ResettableTrackedState();
    map.set(instance, state);
  }
  return state;
}

/**
 * @decorator
 *
 * Marks a field as tracked. Its initializer will be re-run whenever upstream state changes.
 *
 * @example
 *
 * ```js
 * class UserRenameForm {
 *   ⁣@resettableTracked fullName = this.args.fullName;
 *
 *   updateName(newName) {
 *     this.fullName = newName;
 *   }
 * }
 * ```
 *
 * `this.fullName` will be updated whenever `updateName()` is called, or there is a change to
 * `this.args.fullName`.
 *
 */
export function resettableTracked(prototype, key, descriptor) {
  // One WeakMap per-property-per-class. Keys are instances of the class
  const states = new WeakMap();

  return {
    get() {
      const state = getOrCreateState(states, this);

      const upstreamValue = descriptor.initializer?.call(this);

      if (upstreamValue !== state.previousUpstreamValue) {
        state.currentValue = upstreamValue;
        state.previousUpstreamValue = upstreamValue;
      }

      return state.currentValue;
    },

    set(value) {
      const state = getOrCreateState(states, this);
      state.currentValue = value;
    },
  };
}

/**
 * @decorator
 *
 * Same as `@tracked`, but skips notifying about updates if the value is unchanged. This introduces some
 * performance overhead, so should only be used where excessive downstream re-evaluations are a problem.
 *
 * @example
 *
 * ```js
 * class UserRenameForm {
 *   ⁣@dedupeTracked fullName;
 * }
 *
 * const form = new UserRenameForm();
 * form.fullName = "Alice"; // Downstream consumers will be notified
 * form.fullName = "Alice"; // Downstream consumers will not be re-notified
 * form.fullName = "Bob"; // Downstream consumers will be notified
 * ```
 *
 */
export function dedupeTracked(target, key, desc) {
  let { initializer } = desc;
  let { get, set } = tracked(target, key, desc);

  let values = new WeakMap();

  return {
    get() {
      if (!values.has(this)) {
        let value = initializer?.call(this);
        values.set(this, value);
        set.call(this, value);
      }

      return get.call(this);
    },

    set(value) {
      if (!values.has(this) || values.get(this) !== value) {
        values.set(this, value);
        set.call(this, value);
      }
    },
  };
}

export class DeferredTrackedSet {
  #set;

  constructor(value) {
    this.#set = new TrackedSet(value);
  }

  has(value) {
    return this.#set.has(value);
  }

  entries() {
    return this.#set.entries();
  }

  keys() {
    return this.#set.keys();
  }

  values() {
    return this.#set.values();
  }

  forEach(fn) {
    return this.#set.forEach(fn);
  }

  get size() {
    return this.#set.size;
  }

  [Symbol.iterator]() {
    return this.#set[Symbol.iterator]();
  }

  get [Symbol.toStringTag]() {
    return this.#set[Symbol.toStringTag];
  }

  add(value) {
    next(() => this.#set.add(value));
    return this;
  }

  delete(value) {
    next(() => this.#set.delete(value));
    return this;
  }

  clear() {
    next(() => this.#set.clear());
  }
}

/**
 * Converts a value to TrackedArray if needed and validates the type.
 *
 * @param {TrackedArray|Array|null|undefined} value - Value to convert
 * @returns {TrackedArray|null|undefined} - Returns the value converted to TrackedArray if needed,
 *                                         or null/undefined if those values were passed
 * @throws {Error} If value is not an array, TrackedArray, null or undefined
 */
function ensureTrackedArray(value, propertyName) {
  if (typeof value === "undefined" || value === null) {
    return value;
  }

  if (value instanceof TrackedArray) {
    return value;
  }

  if (Array.isArray(value)) {
    return new TrackedArray(value);
  }

  throw new Error(
    `[${propertyName}]: Expected an array, TrackedArray, null, or undefined, got ${typeof value}. Value received: ${value}`
  );
}

/**
 * @decorator
 * Same as `@tracked`, but initializes the value as a TrackedArray.
 *
 * @param {Object} target - The target object
 * @param {string|Symbol} key - The property key
 * @param {Object} desc - The property descriptor
 * @returns {Object} Modified property descriptor that wraps arrays in TrackedArray
 *
 * @example
 * class TodoList {
 *   @autoTrackedArray todos = ['Buy milk', 'Walk dog'];
 * }
 */
export function autoTrackedArray(target, key, desc) {
  if (desc.initializer) {
    const originalInitializer = desc.initializer;
    desc.initializer = function () {
      const initialValue = originalInitializer.apply(this);
      return ensureTrackedArray(initialValue, key);
    };
  }

  const { get, set } = tracked(target, key, {
    ...desc,
    enumerable: true,
    configurable: true,
    isTracked: true,
  });

  function autoTrackedArraySetter(value) {
    set.call(this, ensureTrackedArray(value, key));
  }

  // Bridge TrackedArray content changes to the classic computed property tag
  // system. Without this, @computed('prop') only invalidates on reference
  // changes (this.prop = newArray), not on content mutations like push().
  //
  // This manually implements the same bridging that @dependentKeyCompat does
  // (we can't use @dependentKeyCompat directly because it rejects properties
  // that are already @tracked). The getter captures the TrackedArray's
  // collection tag via track(), then updates tagFor(obj, key) so that
  // @computed properties watching this key see content changes.
  function autoTrackedArrayGetter() {
    // Read the value outside track() to avoid a tag cycle. get.call(this)
    // consumes the property's own tag (tagFor(this, key)) via the tracked
    // storage. If we captured that inside track() and then called updateTag
    // on the same tag, it would create a self-referential cycle.
    const value = get.call(this);

    if (value != null) {
      // Only capture the TrackedArray's collection tag. TrackedArray's Proxy
      // get trap for 'length' calls getValue(collection), registering the
      // collection storage as an autotracking dependency. All mutations
      // (push, pop, splice, etc.) dirty this same storage via
      // setValue(collection, null).
      const propertyTag = tagFor(this, key);
      const collectionTag = track(() => value.length);
      updateTag(propertyTag, collectionTag);
    }

    return value;
  }

  // When using EmberObject.create(...), Ember accesses the tracked properties directly
  // through internal references, bypassing the getter/setter defined in this decorator.
  // To work around this, we are using Ember lower level APIs to override the stored references.
  // https://github.com/emberjs/ember.js/blob/d4f7c5c4075bc5b04736e0e965468bdbe6da135c/packages/%40ember/-internals/metal/lib/tracked.ts#L185
  metaFor(target).writeDescriptors(
    key,
    new TrackedDescriptor(autoTrackedArrayGetter, autoTrackedArraySetter)
  );

  return {
    get: autoTrackedArrayGetter,
    set: autoTrackedArraySetter,
    enumerable: true,
    configurable: true,
    isTracked: true,
  };
}

/** @deprecated Use `autoTrackedArray` instead. */
export { autoTrackedArray as trackedArray };

/**
 * Enumerates all tracked property keys from an object instance.
 *
 * **Warning:** This function uses Ember internal APIs (`peekMeta`, `TrackedDescriptor`)
 * which are not part of the public API and may change without notice in future Ember versions.
 *
 * @param {Object} obj - The object instance to enumerate tracked keys from
 * @returns {Array<string>} An array of tracked property keys.
 *                          Returns an empty array if the object has no prototype or no tracked properties.
 *
 * @example
 * class MyClass {
 *   @tracked name = "Alice";
 *   @tracked age = 30;
 *   regularProp = "not tracked";
 * }
 *
 * const instance = new MyClass();
 * const trackedKeys = enumerateTrackedKeys(instance);
 * // Returns: ["name", "age"]
 */
export function enumerateTrackedKeys(obj) {
  const prototype = obj?.constructor?.prototype;
  if (!prototype) {
    return [];
  }

  const result = new Set();

  // Walk the prototype chain to collect tracked keys from parent classes too
  let meta = peekMeta(prototype);
  while (meta) {
    const descriptors = meta._descriptors;

    if (descriptors) {
      for (const [key, desc] of descriptors) {
        if (desc instanceof TrackedDescriptor) {
          result.add(key);
        }
      }
    }

    meta = meta.parent;
  }

  return [...result];
}

/**
 * Enumerates all tracked property entries from an object instance.
 *
 * **Warning:** This function uses Ember internal APIs (`peekMeta`, `TrackedDescriptor`)
 * which are not part of the public API and may change without notice in future Ember versions.
 *
 * @param {Object} obj - The object instance to enumerate tracked entries from
 * @returns {Array<[string, any]>} An array of [key, value] pairs for all tracked properties.
 *                                  Returns an empty array if the object has no prototype or no tracked properties.
 *
 * @example
 * class MyClass {
 *   @tracked name = "Alice";
 *   @tracked age = 30;
 *   regularProp = "not tracked";
 * }
 *
 * const instance = new MyClass();
 * const trackedEntries = enumerateTrackedEntries(instance);
 * // Returns: [["name", "Alice"], ["age", 30]]
 */
export function enumerateTrackedEntries(obj) {
  const keys = enumerateTrackedKeys(obj);
  return keys.map((key) => [key, obj[key]]);
}
